import React, { useEffect, useState } from "react";
import { ThumbsUp, Star } from "lucide-react";
import axios from "axios";
import { getInitials, isValidAvatar, renderStars } from "../../config/uiHelpers";
import { API_URL } from "../../config/api";

/* ------------------ REVIEW CARD (UPDATED TO MATCH NEW FIGMA) ------------------ */
const ReviewCard = ({ name, program, review, review_title, avatar, rating }) => {
  const [imageError, setImageError] = useState(false);
  const hasAvatar = isValidAvatar(avatar, imageError);

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 flex flex-col hover:shadow-md transition-shadow">
      {/* Top Section: Avatar + Name + Program */}
      <div className="flex items-start gap-4 mb-4">
        {/* Avatar */}
        <div className="w-16 h-16 rounded-full overflow-hidden flex-shrink-0">
          {hasAvatar ? (
            <img
              src={avatar}
              alt={name}
              className="w-full h-full object-cover"
              onError={() => setImageError(true)}
            />
          ) : (
            <div className="w-full h-full bg-[#4AACC5] flex items-center justify-center text-white font-bold text-lg">
              {getInitials(name)}
            </div>
          )}
        </div>

        {/* Name + Program Badge */}
        <div className="flex flex-col gap-2 flex-1 min-w-0">
          <h3 className="text-lg font-bold text-zinc-800">{name}</h3>
          <span className="inline-block self-start px-3 py-1 rounded border border-[#4AACC5] text-[#4AACC5] text-sm font-medium capitalize">
            {program}
          </span>
        </div>
      </div>

      {/* Review Text */}
      <p className="text-sm text-neutral-700 leading-relaxed mb-4">{review}</p>

      {/* Bottom Section: Review Title + Stars */}
      <div className="flex flex-col gap-2 mt-auto">
        {/* Review Title with ThumbsUp */}
        <div className="flex items-center gap-2">
          <ThumbsUp className="w-4 h-4 text-zinc-800" />
          <span className="text-sm font-medium text-black">{review_title}</span>
        </div>

        {/* Star Rating */}
        <div className="flex gap-1 items-center">{renderStars(rating, Star)}</div>
      </div>
    </div>
  );
};

/* ------------------ MAIN PAGE ------------------ */
export default function ReviewRatingPage() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const reviewsPerPage = 9;

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("access_token");

      const response = await axios.get(`${API_URL}/course_reviews/my-courses/reviews`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Flatten all reviews
      const allReviews = response.data.data
        .filter(course => course.reviews && course.reviews.length > 0)
        .flatMap(course =>
          course.reviews.map(review => ({
            name: review.user.full_name,
            program: course.title,
            review_title: review.review_title,
            review: review.review_text || "No review text provided",
            avatar: review.user.avatar_url,
            rating: review.rating,
            reviewed_at: review.reviewed_at,
          }))
        );

      // Sort by latest
      allReviews.sort((a, b) => new Date(b.reviewed_at) - new Date(a.reviewed_at));

      setReviews(allReviews);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching reviews:", err);
      setError("Failed to load reviews");
      setLoading(false);
    }
  };

  /* ------------------ PAGINATION ------------------ */
  const indexOfLastReview = currentPage * reviewsPerPage;
  const indexOfFirstReview = indexOfLastReview - reviewsPerPage;
  const currentReviews = reviews.slice(indexOfFirstReview, indexOfLastReview);
  const totalPages = Math.ceil(reviews.length / reviewsPerPage);

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage(p => p - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(p => p + 1);
  };

  /* ------------------ UI STATES ------------------ */
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-cyan-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading reviews...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 text-lg mb-4">{error}</p>
          <button
            onClick={fetchReviews}
            className="px-4 py-2 bg-cyan-600 text-white rounded hover:bg-cyan-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  /* ------------------ MAIN RENDER ------------------ */
  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-2xl font-bold text-zinc-800">Review and Rating</h1>

          {reviews.length > reviewsPerPage && (
            <div className="flex items-center gap-2">
              <button
                onClick={handlePrevPage}
                disabled={currentPage === 1}
                className={`w-10 h-10 bg-white rounded-lg border border-stone-300 flex items-center justify-center transition-colors ${
                  currentPage === 1 ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-50"
                }`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              <button
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                className={`w-10 h-10 bg-white rounded-lg border border-stone-300 flex items-center justify-center transition-colors ${
                  currentPage === totalPages ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-50"
                }`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          )}
        </div>

        {/* Reviews Grid */}
        {currentReviews.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {currentReviews.map((review, index) => (
              <ReviewCard key={index} {...review} />
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Star className="w-8 h-8 text-gray-400" />
            </div>
            <p className="text-gray-500 text-lg">No reviews available yet</p>
            <p className="text-gray-400 text-sm mt-2">Reviews will appear here once students submit them</p>
          </div>
        )}
      </div>
    </div>
  );
}